import React, { useEffect, useRef } from 'react';
import { useUniqueWcagValues } from 'common-react';
import { Portal } from '@material-ui/core';
import { useFocusTrap } from '../../../../utils/useFocusTrap';
import { ReactComponent as CloseIcon } from 'common-assets/assets/icons/close_icon.svg';
import { useBodyScrollLock } from '../../../../utils/useBodyScrollLock';
import { Typography } from '../../../../Typography/Typography';
import { Button } from '../../../../Button/Button';
import { useTranslation } from 'react-i18next';
import { MobileAppTopBar } from '../../../../MobileAppTopBar/MobileAppTopBar';
import { useTheme } from '../../../../styles/styles';
import useStyles from './AutocompleteModal.style';

const SEARCH_INPUT_SELECTOR = 'input:not([disabled])';

export interface AutocompleteModalProps {
  title: string;
  isOpen: boolean;
  handleClose: () => void;
  modalFooter?: React.ReactNode;
}

const AutocompleteModal: React.FC<AutocompleteModalProps> = ({
  isOpen,
  handleClose,
  title,
  children,
  modalFooter
}) => {
  const c = useStyles({});
  const theme = useTheme();
  const [t] = useTranslation('components');
  const { getUniqueValue } = useUniqueWcagValues();
  const titleId = getUniqueValue('autocomplete-modal-title');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useFocusTrap<HTMLDivElement>(handleClose, isOpen);
  useBodyScrollLock(!isOpen);

  useEffect(() => {
    if (!isOpen) return;

    const trigger = document.activeElement as HTMLElement | null;
    const background = Array.from(document.body.children)
      .filter((element) => element !== dialogRef.current);

    background.forEach((element) => {
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    });

    const focusTimeout = setTimeout(() => {
      const searchInput = dialogRef.current?.querySelector<HTMLElement>(SEARCH_INPUT_SELECTOR);
      (searchInput ?? closeButtonRef.current)?.focus();
    }, 0);

    return () => {
      clearTimeout(focusTimeout);
      background.forEach((element) => {
        element.removeAttribute('aria-hidden');
        element.removeAttribute('inert');
      });

      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [isOpen, dialogRef]);

  if (!isOpen) return null;
  return (
    <Portal>
      <MobileAppTopBar backgroundColor={theme.color.greys[0]} />
      <div ref={dialogRef} className={c.root} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={c.header}>
          <Typography id={titleId} variant="h3" fontWeight="bold">{title || t('autocomplete.modal-title')}</Typography>
          <button ref={closeButtonRef} onClick={handleClose} className={c.closeButton} aria-label={t('autocomplete.close-modal')}><CloseIcon aria-hidden={true} /></button>
        </div>
        <div className={c.content}>
          {children}
        </div>
        <div className={c.modalFooter}>
          <Button buttonType="secondary" onClick={handleClose}>{t('autocomplete.cancel')}</Button>
          {modalFooter && <div>{modalFooter}</div>}
        </div>
      </div>
    </Portal>
  );
};

export default AutocompleteModal;
